import { useQuery } from '@tanstack/react-query';
import { get } from '@/shared/api/api-client';
import type {
  PatientFamilyHistory,
  PatientSocialHistory,
} from '@/types/patient-chart-api';

/**
 * Patient history hooks — Chart > History tab (PROD-736 PR F).
 *
 * History is the union of TWO sub-resources:
 *
 *   GET /api/Patients/GetFamilyMedicalHistoryByPatientId?PatientId={id}
 *       → PatientFamilyHistory[]  (relatives' diagnoses + demographics)
 *
 *   GET /api/Patients/GetSocialHistoryByPatientId?PatientId={id}
 *       → PatientSocialHistory[]  (smoking / alcohol / exercise / etc.)
 *
 * Both flat arrays; tombstones included and filtered client-side.
 * Flutter ref: `patient_chart_data_source_impl.dart:606` (family) +
 * `:703` (social).
 */

export function usePatientFamilyHistory(patientId: string | null | undefined) {
  return useQuery({
    queryKey: ['patient-family-history', patientId],
    queryFn: ({ signal }) => {
      const qs = new URLSearchParams({ PatientId: patientId ?? '' });
      return get<PatientFamilyHistory[]>(
        `/api/Patients/GetFamilyMedicalHistoryByPatientId?${qs.toString()}`,
        { signal },
      );
    },
    enabled: !!patientId,
    staleTime: 60_000,
  });
}

export function usePatientSocialHistory(patientId: string | null | undefined) {
  return useQuery({
    queryKey: ['patient-social-history', patientId],
    queryFn: ({ signal }) => {
      const qs = new URLSearchParams({ PatientId: patientId ?? '' });
      return get<PatientSocialHistory[]>(
        `/api/Patients/GetSocialHistoryByPatientId?${qs.toString()}`,
        { signal },
      );
    },
    enabled: !!patientId,
    staleTime: 60_000,
  });
}
